/**
 * Survey Categories Hook
 *
 * Loads MN ALF survey readiness categories from backend regulatory
 * configs. Categories correspond to MN DHS survey protocol sections,
 * each linked to statutes (144G, 626.557, 245D) and FERP rulesets.
 *
 * Data flow (target):
 *   RegulatoryNodes (rn-*) + RuleSets (rs-4-1-*) → GraphQL query
 *     → Survey categories with compliance status per item
 *
 * Current state: Uses useRegulatoryCompliance for framework data.
 * Falls back to structured reference data until regulatoryScope
 * resolver is wired (backward compat pattern per CLAUDE.md).
 *
 * FERP Principles:
 * - Data-driven: Categories from regulatory config, not hardcoded
 * - Repository pattern: Data through GraphQL hooks
 * - Life-critical: Survey compliance = license retention
 */

import { useMemo } from 'react';
import { gql, useQuery } from '@ferp/graphql-client';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SurveyCategory {
  id: string;
  name: string;
  statute: string;
  totalItems: number;
  description: string;
  rulesetRef: string;
}

export interface SurveyComplianceStatus {
  categoryId: string;
  compliantCount: number;
  inProgressCount: number;
  deficientCount: number;
  notAssessedCount: number;
}

export interface UseSurveyCategoriesResult {
  categories: SurveyCategory[];
  totalItems: number;
  statuses: SurveyComplianceStatus[];
  loading: boolean;
  error: Error | null;
}

// ─── GraphQL Query ────────────────────────────────────────────────────────────

const SURVEY_CATEGORIES_QUERY = gql`
  query SurveyCategories($subgraph: String!, $setting: String) {
    surveyCategories(subgraph: $subgraph, setting: $setting) {
      id
      name
      statute
      totalItems
      description
      rulesetRef
    }
    surveyComplianceStatus(subgraph: $subgraph, setting: $setting) {
      categoryId
      compliantCount
      inProgressCount
      deficientCount
      notAssessedCount
    }
  }
`;

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useSurveyCategories(options: {
  subgraph: string;
  setting?: string;
}): UseSurveyCategoriesResult {
  const { data, loading, error } = useQuery<{
    surveyCategories: SurveyCategory[];
    surveyComplianceStatus: SurveyComplianceStatus[];
  }>(SURVEY_CATEGORIES_QUERY, {
    variables: options,
    errorPolicy: 'all',
  });

  // Backend data preferred. Fallback to seeded reference data
  // until surveyCategories resolver is wired.
  // TODO: Remove fallback when resolver is live (DRR-070 backward compat pattern)
  const categories = data?.surveyCategories ?? REFERENCE_CATEGORIES;
  const statuses = data?.surveyComplianceStatus ?? [];

  const totalItems = useMemo(
    () => categories.reduce((sum, cat) => sum + cat.totalItems, 0),
    [categories],
  );

  return {
    categories,
    totalItems,
    statuses,
    loading,
    error: error ?? null,
  };
}

// ─── Reference Data (seeded from regulatory node configs) ────────────────────
// Maps to MN DHS ALF survey protocol. Each category corresponds to
// statute sections and FERP rulesets (rs-4-1-004, rs-4-1-005, rs-4-1-006).
// This data is the fallback until the regulatoryScope GraphQL resolver
// serves it directly from the seeded configs.

const REFERENCE_CATEGORIES: SurveyCategory[] = [
  { id: 'resident-rights', name: 'Resident Rights', statute: '144G.08 Subd 1-6', totalItems: 15, description: 'Privacy, dignity, communication, grievance procedures', rulesetRef: 'rs-4-1-004' },
  { id: 'service-plans', name: 'Individualized Service Plans', statute: '144G.08 Subd 7', totalItems: 12, description: 'Assessment within 14 days, QP development, annual review, resident participation', rulesetRef: 'rs-4-1-004' },
  { id: 'medication-management', name: 'Medication Management', statute: '144G.71', totalItems: 18, description: 'Storage, administration, delegation, controlled substances, eMAR, pharmacy coordination', rulesetRef: 'rs-4-1-004' },
  { id: 'staffing', name: 'Staffing Requirements', statute: '144G.41-43', totalItems: 10, description: 'ALD qualifications, RN oversight, staff ratios, training requirements', rulesetRef: 'rs-4-1-004' },
  { id: 'abuse-prevention', name: 'Abuse Prevention & Reporting', statute: '626.557', totalItems: 14, description: 'Background studies, mandated reporting, MAARC procedures, investigation protocols', rulesetRef: 'rs-4-1-005' },
  { id: 'infection-control', name: 'Infection Control', statute: '144G.08 Subd 8', totalItems: 8, description: 'Hand hygiene, PPE, TB screening, outbreak protocols', rulesetRef: 'rs-4-1-004' },
  { id: 'fire-life-safety', name: 'Fire & Life Safety', statute: 'MN 4658', totalItems: 12, description: 'Fire drills, exit lights, extinguishers, evacuation plans, smoke detectors', rulesetRef: 'rs-4-1-004' },
  { id: 'dietary', name: 'Dietary Services', statute: '144G.08 Subd 7(a)(4)', totalItems: 8, description: 'Meal service, special diets, food safety, hydration', rulesetRef: 'rs-4-1-004' },
  { id: 'environment', name: 'Physical Environment', statute: '144G.45', totalItems: 10, description: 'Building codes, ADA compliance, maintenance, cleanliness', rulesetRef: 'rs-4-1-004' },
  { id: 'documentation', name: 'Documentation & Records', statute: '144G.08 Subd 9', totalItems: 8, description: 'Medical records, incident reports, care documentation, retention policies', rulesetRef: 'rs-4-1-004' },
  { id: 'hcbs-245d', name: 'HCBS / 245D Overlay', statute: '245D', totalItems: 8, description: 'Coordinated support plans, lead agency coordination, rights restrictions', rulesetRef: 'rs-4-1-006' },
  { id: 'qa-pi', name: 'Quality Assurance', statute: '144G.08 Subd 10', totalItems: 5, description: 'QAPI program, incident trending, corrective action plans, self-assessments', rulesetRef: 'rs-4-1-004' },
];
